/**
 * MusicPlayer (#90 phase 1) — invisible YouTube IFrame for background music.
 *
 * Why an iframe and not the YouTube Data API:
 *   - YouTube Data API has a quota; embedding doesn't. ToS allows it.
 *   - Brave's video search (server-side) gives us the YouTube id.
 *   - Web Audio API would need transcoded audio files; we don't have those.
 *
 * Why imperative ref API instead of declarative props:
 *   - Music is fundamentally stateful (currently playing or not, position).
 *   - Re-rendering the iframe with a different src restarts it from scratch
 *     and loses position; ref-based commands keep the same iframe alive.
 *   - The pause-during-Berta-TTS coordination is event-driven and reads
 *     more naturally as imperative calls.
 *
 * Imperative API exposed via React.forwardRef:
 *   - play(youtubeId, title)  — load + play a new track
 *   - stop()                  — stop and clear
 *   - pause()                 — pause without clearing (Berta is speaking)
 *   - resume()                — resume from where pause() left off
 *   - getState()              — { playing, paused, youtubeId, title }
 *
 * Visibility: the iframe is positioned 1×1 px in the corner. Audio still
 * plays; the user never sees the YouTube branding or controls.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

// YouTube IFrame API loads asynchronously. The global function name is
// fixed by YouTube; we register ours via a ready-callback queue so multiple
// instances of this component (shouldn't happen, but) cooperate.
let ytApiReadyPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (ytApiReadyPromise) return ytApiReadyPromise;
  ytApiReadyPromise = new Promise(resolve => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve();
    };
  });
  return ytApiReadyPromise;
}

const MusicPlayer = forwardRef(function MusicPlayer(props, ref) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  // Set true once the YouTube IFrame API fires onReady. Imperative
  // commands (play/pause/resume/stop) are dropped before this. Without
  // it, calling pauseVideo() before the iframe has fully loaded triggers
  // the "postMessage on DOMWindow: target origin does not match" warning
  // we saw in production logs.
  const readyRef = useRef(false);
  // Track public state so the parent can render a "now playing" indicator
  // by also receiving a callback. Internal state is kept on the YT player.
  const stateRef = useRef({ playing: false, paused: false, youtubeId: null, title: null });
  // #459 follow-up — mirror the user's preferred volume so we can
  // apply it in onReady (matches VideoOverlay's pattern). Without
  // this, the iframe always starts at YouTube's default 100 even
  // when MusicView's slider says e.g. 70 — clicks change state
  // but the playing audio is at 100 until the user moves the
  // slider, which feels like the slider isn't working.
  const desiredVolumeRef = useRef(70);
  const [, forceRender] = useState(0);

  function broadcast() {
    if (props.onStateChange) props.onStateChange({ ...stateRef.current });
    forceRender(n => n + 1);
  }

  // #475 — deps MUST be `[]` (mount-only). Pre-#475 this was `[props]`,
  // which is a new object every render, so every TabletApp re-render
  // (any state change during a call — connection_hiccup, transcript
  // update, idle-dim, etc.) re-fired this effect, creating a NEW
  // YouTube iframe each time. The old iframe was never destroyed
  // (cleanup only flipped a `cancelled` flag), so iframes piled up
  // — Bas counted 14 on his tablet after a day of staging testing.
  // Worse, `playerRef.current` was reassigned to the latest iframe
  // each time, so if music was loaded on iframe #5 via
  // `loadVideoById` and then a re-render created iframe #6,
  // setVolume / stop / etc. all targeted #6 (silent — videoId='')
  // while music kept playing on orphaned iframe #5 with no React
  // handle on it. That's the root cause of the #462 "volume slider
  // doesn't change audio" bug AND likely contributed to the #458
  // "stop doesn't reliably silence music" bug. The effect body
  // doesn't actually USE props anywhere — the `[props]` dep was
  // wrong from the start.
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(() => {
      if (cancelled || !containerRef.current) return;
      // Create a placeholder div the YT API will replace with an iframe
      const inner = document.createElement('div');
      inner.id = `yt-music-player-${Math.random().toString(36).slice(2)}`;
      containerRef.current.appendChild(inner);
      playerRef.current = new window.YT.Player(inner.id, {
        height: '1',
        width: '1',
        videoId: '',
        // origin matches the iframe's parent — required to suppress the
        // "postMessage origin does not match" warning that fires while
        // the iframe is loading.
        host: 'https://www.youtube.com',
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            // Iframe is fully loaded; postMessage will succeed from now on.
            // Imperative methods (play/pause/etc) become safe.
            readyRef.current = true;
            // Apply the desired volume the moment the iframe is ready,
            // matching VideoOverlay's pattern. Without this the iframe
            // sits at YouTube's default 100 until the user moves the
            // slider, which made volume +/- clicks feel ineffective
            // (the audio was already at max).
            try {
              if (e && e.target && e.target.setVolume) {
                e.target.setVolume(desiredVolumeRef.current);
              }
            } catch (_) {}
          },
          onStateChange: e => {
            // YT.PlayerState: -1 unstarted, 0 ended, 1 playing, 2 paused,
            // 3 buffering, 5 cued
            const s = e.data;
            if (s === 0) {
              // Track ended naturally — clear state
              stateRef.current = { playing: false, paused: false, youtubeId: null, title: null };
              broadcast();
            } else if (s === 1) {
              stateRef.current = { ...stateRef.current, playing: true, paused: false };
              broadcast();
            } else if (s === 2) {
              stateRef.current = { ...stateRef.current, playing: false, paused: true };
              broadcast();
            }
          },
          onError: () => {
            // Playback error (e.g. video not embeddable). Clear state so the
            // pill disappears rather than showing stale "now playing".
            stateRef.current = { playing: false, paused: false, youtubeId: null, title: null };
            broadcast();
          },
        },
      });
    });
    // #475 cleanup: when MusicPlayer unmounts (or React strict-mode
    // dev-double-mounts), destroy the YT.Player so the iframe is
    // removed from the DOM. Without this, even a single legitimate
    // unmount would leak. The `cancelled` flag still handles the
    // case where unmount fires before loadYouTubeApi() resolves.
    return () => {
      cancelled = true;
      const p = playerRef.current;
      if (p && typeof p.destroy === 'function') {
        try { p.destroy(); } catch (_) {}
      }
      playerRef.current = null;
      readyRef.current = false;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    play(youtubeId, title) {
      const p = playerRef.current;
      // loadVideoById is the one method that's safe to call even before
      // onReady — YouTube buffers the request internally and processes
      // it once the iframe loads.
      if (!p || !p.loadVideoById) return;
      // The new robust stop() (above) calls mute() to silence any
      // residual audio. unMute here so a subsequent play after a stop
      // isn't silent.
      try { if (p.unMute) p.unMute(); } catch (_) {}
      p.loadVideoById({ videoId: youtubeId });
      stateRef.current = { playing: true, paused: false, youtubeId, title: title || null };
      broadcast();
    },
    stop() {
      const p = playerRef.current;
      // #448 → bug-still-there report (2026-05-08): stopVideo() alone
      // is unreliable on iOS Safari and during transitional states
      // (buffering / error / immediately-after-load) — the iframe
      // honours the postMessage but lags long enough that the user
      // hears the music continue even after the visible player closes.
      // Belt-and-braces here:
      //   1. mute()       — silences audio immediately, even if the
      //                     subsequent stop is slow.
      //   2. stopVideo()  — primary stop (cancels playback + loading).
      //   3. clearVideo() — drops the cued/playing video entirely so
      //                     the iframe can't accidentally resume.
      // Each call is independently try-guarded — partial successes are
      // better than abandoning the rest of the chain on the first
      // method that throws (some YT states throw on mute, others on
      // clearVideo, etc.).
      // We also relax the readyRef gate: even when the iframe says
      // "not ready", postMessage attempts are cheap, and silencing a
      // possibly-already-playing track is the right thing to do.
      try { if (p && p.mute)       p.mute();       } catch (_) {}
      try { if (p && p.stopVideo)  p.stopVideo();  } catch (_) {}
      try { if (p && p.clearVideo) p.clearVideo(); } catch (_) {}
      stateRef.current = { playing: false, paused: false, youtubeId: null, title: null };
      broadcast();
    },
    pause() {
      const p = playerRef.current;
      if (!p || !p.pauseVideo || !readyRef.current) return;
      // Only pause if currently playing — pausing a non-playing iframe is
      // harmless but causes spurious onStateChange events.
      if (stateRef.current.playing) p.pauseVideo();
    },
    resume() {
      const p = playerRef.current;
      if (!p || !p.playVideo || !readyRef.current) return;
      // Only resume if we were paused with a track loaded
      if (stateRef.current.paused && stateRef.current.youtubeId) p.playVideo();
    },
    getState() {
      return { ...stateRef.current };
    },
    // #448 — volume control for the new full-screen MusicView. YouTube
    // iframe API takes ints 0..100.
    //
    // Bas's report 2026-05-08: clicking +/- didn't change volume.
    // Two fixes:
    //   1. Drop the readyRef gate. setVolume is cheap (postMessage to
    //      the iframe); attempting it before onReady was harmless and
    //      the gate created a race where a click during buffering /
    //      transitional state was silently dropped.
    //   2. unMute() before setVolume — the new robust stop() (just
    //      shipped) calls mute() to silence audio reliably. If the
    //      user adjusts volume on a track that was previously stopped,
    //      the iframe might still be muted; setting volume = 70 on a
    //      muted player has no audible effect. unMute first.
    //
    // Note: on iOS / Android tablets the YouTube IFrame API treats
    // setVolume() as a no-op because the OS owns the output volume —
    // hardware buttons are the only thing that actually changes
    // playback volume. The MusicView UI shows a "use device volume"
    // hint on touch devices to set expectations honestly.
    setVolume(level) {
      const p = playerRef.current;
      const clamped = Math.max(0, Math.min(100, Math.round(level)));
      // Always remember the desired volume so onReady (next time the
      // iframe loads / re-loads) can apply it from the start.
      desiredVolumeRef.current = clamped;
      if (!p) return;
      try { if (p.unMute)   p.unMute();          } catch (_) {}
      try { if (p.setVolume) p.setVolume(clamped); } catch (_) {}
    },
    getVolume() {
      const p = playerRef.current;
      if (!p || !p.getVolume) return 50;
      try { return p.getVolume(); } catch (_) { return 50; }
    },
  }), []);

  // Portal to document.body so the iframe persists across TabletApp's
  // conditional render branches (idle / calling / connected). Mounting
  // inside a conditional branch would unmount the iframe on transition,
  // killing playback every time the callState changed.
  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        opacity: 0,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />,
    document.body
  );
});

export default MusicPlayer;
